import React, { FC, useEffect, useState } from 'react'
import styled from 'styled-components'
import { SNLogoInBigCircleIcon } from '../../assets/icons'
import { DescriptionTextStyled, SecondaryHeaderStyled } from '../../styles/shared'
import { PlatformsDownload } from '../common'
import HorizontalSeparator from '../common/horizontalSeparator'
import TextWithLinkStyled from '../common/textWithLinkStyled'
import IconsContainer from './iconsContainer'
import { ScreenBreakpointSize } from '../../utils/enums'
import { getScreenBreakpointSize } from '../../utils/getScreenBreakpointSize'
import { THandleDownload } from '../../utils/handleDownload'
import { IGetDownloadDataReturnItem } from '../../utils/getDownloadData'

interface IProps {
  platformItems: IGetDownloadDataReturnItem[]
  handleDownload: THandleDownload
  handlePlatformItemChange: (newPlatformItemIndex: number) => void
  currentPlatformItemIndex: number | undefined
}

const MainContainerStyled = styled.div`
  overflow-x: clip;
`
const ContainerStyled = styled.div`
  background: linear-gradient(180deg, rgba(248, 249, 252, 0) 20%, var(--background-gray) 100%);
  margin: var(--spacing-72) auto 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
`
const CustomSnLogoIconStyled = styled.img`
  margin-top: 6%;
  margin-bottom: var(--spacing-24);

  @media screen and (max-width: ${getScreenBreakpointSize(ScreenBreakpointSize.Medium, -1)}) {
    margin-top: 48%;
    height: 172px;
    width: 172px;
  }

  @media screen and (min-width: ${getScreenBreakpointSize(
      ScreenBreakpointSize.Medium,
    )}) and (max-width: ${getScreenBreakpointSize(ScreenBreakpointSize.Large, -1)}) {
    margin-top: 20%;
  }

  @media screen and (min-width: ${getScreenBreakpointSize(
      ScreenBreakpointSize.Large,
    )}) and (max-width: ${getScreenBreakpointSize(ScreenBreakpointSize.ExtraLarge, -1)}) {
    margin-top: 12%;
  }
`
const CustomSectionTitleStyled = styled(SecondaryHeaderStyled)`
  max-width: 85%;
  text-align: center;

  &:not(:first-child) {
    margin-top: 0;
    margin-bottom: 0;
  }

  @media screen and (max-width: ${getScreenBreakpointSize(ScreenBreakpointSize.Medium, -1)}) {
    max-width: 60%;
  }
`
const CustomDescriptionTextStyled = styled(DescriptionTextStyled)`
  margin-top: var(--spacing-16);
  margin-bottom: var(--spacing-24);

  @media screen and (max-width: ${getScreenBreakpointSize(ScreenBreakpointSize.Large, -1)}) {
    max-width: 70%;
    text-align: center;
  }

  @media screen and (min-width: ${getScreenBreakpointSize(ScreenBreakpointSize.Large)}) {
    max-width: none;
  }
`
const DownloadSectionContainer = styled.div<{ platformItem: IGetDownloadDataReturnItem | null }>`
  display: flex;
  gap: var(--spacing-10);

  @media screen and (max-width: ${getScreenBreakpointSize(ScreenBreakpointSize.Medium, -1)}) {
    display: block;
    width: 100%;
    padding-left: var(--spacing-16);
    padding-right: var(--spacing-16);
  }

  @media screen and (min-width: ${getScreenBreakpointSize(
      ScreenBreakpointSize.Medium,
    )}) and (max-width: ${getScreenBreakpointSize(ScreenBreakpointSize.Large, -1)}) {
    display: block;
  }
`

const CustomHorizontalSeparatorStyled = styled(HorizontalSeparator)`
  margin-top: var(--spacing-48);
  width: 27%;

  @media screen and (max-width: ${getScreenBreakpointSize(ScreenBreakpointSize.Large, -1)}) {
    display: none;
  }

  @media screen and (min-width: ${getScreenBreakpointSize(
      ScreenBreakpointSize.Large,
    )}) and (max-width: ${getScreenBreakpointSize(ScreenBreakpointSize.ExtraLarge, -1)}) {
    width: 30%;
  }
`
const WantMoreSectionStyled = styled.div`
  display: flex;
  justify-content: center;
  margin-top: var(--spacing-48);
  margin-bottom: var(--spacing-72);

  @media screen and (max-width: ${getScreenBreakpointSize(ScreenBreakpointSize.Large, -1)}) {
    margin-top: var(--spacing-24);
    margin-bottom: var(--spacing-48);
  }
`
const WantMoreTextStyled = styled(DescriptionTextStyled)`
  font-size: var(--font-size-16);
  margin-left: var(--spacing-8);
`
const LinkContainerStyled = styled.div`
  margin-top: var(--spacing-3);
  margin-left: var(--spacing-8);
`
const DownloadSection: FC<IProps> = ({
  platformItems,
  currentPlatformItemIndex,
  handleDownload,
  handlePlatformItemChange,
}) => {
  const [platformItem, setPlatformItem] = useState<IGetDownloadDataReturnItem | null>(null)

  useEffect(() => {
    if (typeof currentPlatformItemIndex === 'undefined') {
      return
    }
    setPlatformItem(platformItems[currentPlatformItemIndex])
  }, [currentPlatformItemIndex, platformItems])

  return (
    <MainContainerStyled>
      <ContainerStyled>
        <CustomSnLogoIconStyled alt="SN Logo In Big Circle Icon" src={SNLogoInBigCircleIcon} />
        <CustomSectionTitleStyled>Download Standard Notes</CustomSectionTitleStyled>
        <CustomDescriptionTextStyled>Unlimited notes, unlimited devices, all for free.</CustomDescriptionTextStyled>
        <DownloadSectionContainer platformItem={platformItem}>
          <PlatformsDownload
            platformItems={platformItems}
            handleDownload={handleDownload}
            handlePlatformItemChange={handlePlatformItemChange}
          />
        </DownloadSectionContainer>
        <CustomHorizontalSeparatorStyled />
        <WantMoreSectionStyled>
          <WantMoreTextStyled>Want more?</WantMoreTextStyled>
          <LinkContainerStyled>
            <TextWithLinkStyled text={'Learn about our plans'} targetUrl={'/plans'} />
          </LinkContainerStyled>
        </WantMoreSectionStyled>
        <IconsContainer />
        <HorizontalSeparator />
      </ContainerStyled>
    </MainContainerStyled>
  )
}

export default DownloadSection
